import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { ItemMediaList } from '@euler/app/flows/task/components/ItemMediaList';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { wrapTaskNavigatorScreen } from '@euler/app/flows/task/wrapTaskNavigatorScreen';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { isMobileLayout } from '@euler/functions/responsiveness';
import { DeliveryCheckSceneType } from '@euler/model/enum';
import { DeliveryCheckItemStaged } from '@euler/model/task-detail';
import { formatDateTime } from '@euler/utils/formatters';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { memo, useCallback } from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Props = {
  sceneType: DeliveryCheckSceneType;
};

export const ReworkRecordsPage = wrapTaskNavigatorScreen(
  ({ sceneType }: Props) => {
    const insets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();

    const {
      detail,
      taskManager: { deliveryCheckManager, samplingCheckManager, extraInfo$ },
    } = useTaskContext();

    const manager =
      sceneType === DeliveryCheckSceneType.CompletionCheck
        ? deliveryCheckManager
        : samplingCheckManager;

    const [staged] = useBehaviorSubject(manager.staged$);

    return (
      <StackLayout
        gap={rpx(15)}
        css={`
          flex: 1;
          padding: ${rpx(15)}px 0;
        `}
      >
        <StackLayout
          direction="row"
          alignItems="center"
          gap={rpx(15)}
          backgroundColor={'#fff'}
          padding={rpx(15)}
        >
          <Img
            uri={detail.basicInfo.vehicleImageUrl}
            width={rpx(90)}
            height={rpx(60)}
            source={
              detail.basicInfo.vehicleImageUrl
                ? undefined
                : require('@euler/assets/img/default-car-img.png')
            }
          />
          <StackLayout flex={1} gap={rpx(5)}>
            <StackLayout direction="row" alignItems="center" gap={rpx(8)}>
              <Typography.Text black size={rpx(18)}>
                {detail.basicInfo.licensePlateNo}
              </Typography.Text>
              <Center
                css={`
                  border-radius: ${rpx(2)}px;
                  border-width: ${rpx(1)}px;
                  border-color: rgb(32, 127, 231);
                  padding: ${rpx(0)}px ${rpx(5)}px;
                `}
              >
                <Typography.Text
                  color={'rgb(32, 127, 231)'}
                  size={rpx(15)}
                  bold
                >
                  {detail.basicInfo.vehicleModel}
                </Typography.Text>
              </Center>
            </StackLayout>
            <StackLayout
              direction="row"
              alignItems="center"
              divider={<Typography.Text color="gray">|</Typography.Text>}
              gap={rpx(5)}
            >
              <Typography.Text opacity={0.8}>
                服务次数: {extraInfo$.value?.serviceRecordCount ?? 0}
              </Typography.Text>
              <Typography.Text opacity={0.8}>
                {detail.basicInfo.vehicleMileage}KM
              </Typography.Text>
              <Typography.Text opacity={0.8}>
                业务员: {detail.basicInfo.serviceAgentName}
              </Typography.Text>
            </StackLayout>
          </StackLayout>
        </StackLayout>
        <ScrollView contentContainerStyle={{ paddingBottom: insets.bottom }}>
          <StackLayout gap={rpx(15)}>
            {staged.items
              .filter(x => x.reworkRecords.length)
              .map(item => (
                <Cell item={item} key={item._key} />
              ))}
          </StackLayout>
        </ScrollView>
      </StackLayout>
    );
  },
  () =>
    isMobileLayout() && Platform.OS !== 'web'
      ? {
          headerShown: true,
          title: '内返记录',
          presentation: 'modal',
          headerStatusBarHeight: undefined,
        }
      : {},
);

const Cell = memo(
  ({
    item,
    style,
  }: {
    item: DeliveryCheckItemStaged;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const appNavigation = useAppNavigation();
    const labelStyle: TextStyle = { opacity: 0.7, fontWeight: '400' };

    const onPreviewVideo = useCallback(
      (media: MediaInfo) => {
        appNavigation.navigate('_mediaPreview', {
          type: 'video',
          url: media.url,
        });
      },
      [appNavigation],
    );

    return (
      <StackLayout
        gap={rpx(10)}
        backgroundColor={'#fff'}
        padding={rpx(15)}
        style={style}
        divider={
          <View
            css={`
              height: 1px;
              background-color: rgb(237, 237, 237);
            `}
          />
        }
      >
        <StackLayout gap={rpx(8)}>
          <Typography.Text bold>
            <Text style={labelStyle}>检验项目: </Text>
            {item.title}
          </Typography.Text>
          <Typography.Text>
            <Text style={labelStyle}>检验时间: </Text>
            {formatDateTime(item.timestamp ?? new Date())}
          </Typography.Text>
          <Typography.Text>
            <Text style={labelStyle}>检验人员: </Text>
            {item.authorName}
          </Typography.Text>
        </StackLayout>

        {/** rework record info */}
        {item.reworkRecords.map(record => (
          <StackLayout
            key={record._key}
            gap={rpx(10)}
            divider={
              <View
                css={`
                  height: 1px;
                  background-color: rgb(237, 237, 237);
                `}
              />
            }
          >
            <StackLayout gap={rpx(8)}>
              <StackLayout
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography.Text>
                  <Text style={labelStyle}>责任人员: </Text>
                  <Text
                    style={{
                      color: record.responsiblePersonName
                        ? '#000'
                        : StatusColors.Danger,
                    }}
                  >
                    {record.responsiblePersonName || '(未知)'}
                  </Text>
                </Typography.Text>
              </StackLayout>

              <StackLayout direction="row" alignItems="center">
                <Typography.Text style={labelStyle}>车辆问题: </Typography.Text>
                <Typography.Text
                  color="rgb(154, 156, 158)"
                  light
                  size={rpx(12)}
                >
                  内返项目不对外展示仅用于门店记录
                </Typography.Text>
              </StackLayout>
            </StackLayout>

            <StackLayout gap={rpx(12)}>
              <ItemMediaList
                medias={record.medias.map(mediaStagedToMediaInfo)}
                columns={4}
                editable={false}
                onPreviewVideo={onPreviewVideo}
              />

              <StackLayout gap={rpx(5)} direction="row">
                <Typography.Text style={labelStyle}>备注: </Typography.Text>
                <Typography.Text>{record.remarks || '无'}</Typography.Text>
              </StackLayout>
            </StackLayout>
          </StackLayout>
        ))}
      </StackLayout>
    );
  },
);
